import logger from '../utils/logger';
import { useEffect, useRef } from 'react';
import { useBaristaSettings } from '../context/BaristaSettingsContext';
import { notificationTones, playTone } from '../utils/notificationTones';

export const useNotificationSound = () => {
  const { settings } = useBaristaSettings();
  const repeatIntervalRef = useRef(null);
  const settingsRef = useRef(settings);

  // Always keep the latest settings in the ref
  useEffect(() => {
    settingsRef.current = settings;
  logger.debug('Settings updated in notification hook:', settings);
  }, [settings]);

  const playNotificationSound = () => {
    // Always use the latest settings from the ref
    const currentSettings = settingsRef.current;
  logger.debug('playNotificationSound called with settings:', {
      soundEnabled: currentSettings.soundEnabled,
      notificationTone: currentSettings.notificationTone,
      volume: currentSettings.volume
    });
    
    if (currentSettings.soundEnabled) {
      const volume = currentSettings.volume / 100;
  logger.debug('Playing tone:', currentSettings.notificationTone, 'at volume:', volume);
      playTone(currentSettings.notificationTone, volume).catch(err => 
  logger.warn('Failed to play notification tone:', err)
      );

      // Handle repeating notifications
      if (currentSettings.repeatNotifications && currentSettings.repeatInterval) {
        // Clear any existing interval
        if (repeatIntervalRef.current) {
          clearInterval(repeatIntervalRef.current);
        }

        // Set up repeating notifications with a maximum of 3 repetitions
        const maxRepetitions = 3;
        let repetitionCount = 0;
        
        repeatIntervalRef.current = setInterval(() => {
          repetitionCount++;
          const latestSettings = settingsRef.current;
          
          if (latestSettings.soundEnabled && repetitionCount <= maxRepetitions) {
            const volume = latestSettings.volume / 100;
            playTone(latestSettings.notificationTone, volume).catch(err => 
              logger.warn('Failed to play repeat notification tone:', err)
            );
          }
          
          // Stop repeating after max repetitions
          if (repetitionCount >= maxRepetitions) {
            if (repeatIntervalRef.current) {
              clearInterval(repeatIntervalRef.current);
              repeatIntervalRef.current = null;
            }
          }
        }, (currentSettings.repeatInterval || 3) * 1000);
      }
    }
  };

  const testNotificationSound = () => {
    const currentSettings = settingsRef.current;
    if (currentSettings.soundEnabled) {
      const volume = currentSettings.volume / 100;
      playTone(currentSettings.notificationTone, volume).catch(err => 
  logger.warn('Failed to play test notification tone:', err)
      );
    }
  };



  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (repeatIntervalRef.current) {
        clearInterval(repeatIntervalRef.current);
      }
    };
  }, []);

  return { 
    playNotificationSound, 
    testNotificationSound,
    notificationTones
  };
};
